import { ChatResponseOption } from '@akin-home/chat';
import { Text } from '@akin-home/text/native';
import React from 'react';
import styled from 'styled-components/native';

type Props = {
  options: ChatResponseOption[];
  selections?: { [x: string]: string };
  onPressOption(option: ChatResponseOption, value: string): void;
  onPressOtherEmotion(): void;
  other_emotion_label?: string;
  other_emotion_input?: string;
};

export function EmotionPickerWheel(props: Props) {
  const { other_emotion_label } = props;

  const pairs = formOptionPairs(props.options);

  function onPressOtherEmotion() {
    props.onPressOtherEmotion?.();
  }

  return (
    <Container>
      {pairs.map((pair, idx) => (
        <InputPair
          key={idx}
          style={{
            transform: [{ rotate: `${idx * 18}deg` }],
          }}
        >
          <LeftSet>
            {[...Array(5).keys()].map((idx) => (
              <EmotionCircle
                key={idx}
                index={idx}
                option={pair[0]}
                selected={
                  (props.selections?.[pair[0].input.value] ?? '0') >=
                  `${idx + 1}`
                }
                value={`${idx + 1}`}
                onPress={props.onPressOption}
              />
            ))}

            <LeftContainer>
              <Text
                bold
                style={{
                  textAlign: 'right',
                  transform: [{ rotate: `-${idx * 18}deg` }],
                }}
              >
                {pair[0].label}
              </Text>
            </LeftContainer>
          </LeftSet>
          <RightSet>
            {[...Array(5).keys()].map((idx) => (
              <EmotionCircle
                key={idx}
                index={idx}
                option={pair[1]}
                selected={
                  (props.selections?.[pair[1].input.value] ?? '0') >=
                  `${idx + 1}`
                }
                value={`${idx + 1}`}
                onPress={props.onPressOption}
              />
            ))}

            <RightContainer>
              <Text
                bold
                style={{
                  transform: [{ rotate: `-${idx * 18}deg` }],
                }}
              >
                {pair[1].label}
              </Text>
            </RightContainer>
          </RightSet>
        </InputPair>
      ))}
      {other_emotion_label ? (
        <UnknownEmotionContainer onPress={onPressOtherEmotion}>
          <OtherEmotionText weight="semibold">
            {other_emotion_label}
          </OtherEmotionText>
        </UnknownEmotionContainer>
      ) : null}
    </Container>
  );
}

type EmotionRowProps = {
  index: number;
  option: ChatResponseOption;
  value: string;
  selected: boolean;
  onPress(option: ChatResponseOption, value: string): void;
};

function EmotionCircleInner(props: EmotionRowProps) {
  const { index, option, value, selected = false } = props;

  function onPress() {
    props.onPress(option, value);
  }

  return (
    <Circle size={index} onPress={onPress} selected={selected}>
      <CircleInner color={option.color} index={index} />
    </Circle>
  );
}

function formOptionPairs(options: ChatResponseOption[]) {
  const middleIndex = Math.floor(options.length / 2);
  const firstHalf = options.slice(0, middleIndex);
  const secondHalf = options.slice(middleIndex);

  return firstHalf.map((option, idx) => [option, secondHalf[idx]]);
}
const EmotionCircle = React.memo(EmotionCircleInner);

const Container = styled.View`
  align-items: center;
  justify-content: center;
`;

const InputPair = styled.View`
  position: absolute;
  flex-direction: row;
  align-items: center;
  gap: 180px;
  justify-content: center;
`;

const LeftSet = styled.View`
  flex-direction: row-reverse;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
`;

const RightSet = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const Circle = styled.TouchableOpacity<{
  color?: string;
  size: number;
  selected?: boolean;
}>`
  border-radius: 1000px;

  ${({ size }) => `height: ${28 + 5 * size}px; width: ${28 + 5 * size}px;`}

  border-color: black;
  border-width: ${({ selected }) => (selected ? '6px' : '1px')};
  padding: 1px;
  overflow: hidden;

  align-items: center;
  justify-content: center;
`;

const CircleInner = styled.View<{ color?: string; index: number }>`
  height: 120%;
  width: 120%;
  border-radius: 1000px;
  background-color: ${({ color }) => color || 'white'};

  ${({ index }) => `opacity: ${0.6 + 0.1 * index}`};
`;

const LeftContainer = styled.View`
  width: 200px;
  flex-direction: row;
  justify-content: flex-end;
`;

const RightContainer = styled.View`
  width: 200px;
  flex-direction: row;
`;

const UnknownEmotionContainer = styled.TouchableOpacity`
  position: absolute;
  height: 150px;
  width: 150px;
  background-color: lightgrey;
  border-radius: 200px;
  padding: 2px;

  align-items: center;
  justify-content: center;
`;

const OtherEmotionText = styled(Text)`
  text-align: center;
`;
